<script lang="ts">
import { defineBlock } from '@bagelink/blox'
import { defineSchema, $ } from '@bagelink/vue'
const defaults = {
	layout: 'horizontal',
	showPrint: true,
	showShare: true,
	showCite: true,
}
const schema = defineSchema({
	layout: $.select('Layout', [
		{ label: 'Horizontal', value: 'horizontal' },
		{ label: 'Vertical', value: 'vertical' },
	]),
	showPrint: $.toggle('Show Print'),
	showShare: $.toggle('Show Share'),
	showCite: $.toggle('Show Cite'),
})
export const ArticleActions = defineBlock({
	pickerHidden: true,
	thumbnail: '/block-thumbs/ArticleActions.svg',
	category: 'Utility',
	order: 20,
	name: 'ArticleActions',
	label: 'Article Actions',
	icon: 'share',
	schema,
	defaults,
})
</script>
<script setup lang="ts">

import { useRoute } from 'vue-router'
import { articleTitleText } from '@/utils/articleHtml'
import { canonicalUrl } from '@/utils/seo'
import { Btn, Icon, Dialog } from '@bagelink/vue'
import { onMounted, onUnmounted, ref } from 'vue'
import { useBoundPageItem } from '@/composables/useBoundPageItem'




const props = withDefaults(
	defineProps<{
		layout?: 'horizontal' | 'vertical'
		showPrint?: boolean
		showShare?: boolean
		showCite?: boolean
	}>(),
	{
		layout: 'horizontal',
		showPrint: true,
		showShare: true,
		showCite: true,
	},
)

const boundRecord = useBoundPageItem()
const showShareDropdown = ref(false)
const showCiteModal = ref(false)
const copiedCitation = ref<string | null>(null)
let hideTimeout: ReturnType<typeof setTimeout> | null = null

// Print functionality
function handlePrint() {
	const pageTitle = document.title
	const pageContent = document.documentElement.innerHTML

	sessionStorage.setItem('printPageTitle', pageTitle)
	sessionStorage.setItem('printPageContent', pageContent)

	window.open('/print', '_blank')
}

function hidePrintElements() {
	const selectors = [
		'.article-actions',
		'.main-nav',
		'nav',
		'.block-nav',
		'.read-more-btn',
		'footer',
		'.footer',
		'.block-footer',
		'.bot-footer',
		'.hidePrint',
	]

	selectors.forEach(selector => {
		const elements = document.querySelectorAll(selector)
		elements.forEach(el => {
			const htmlEl = el as HTMLElement
			htmlEl.dataset.originalDisplay = htmlEl.style.display || ''
			htmlEl.style.display = 'none'
		})
	})

	const maskElements = document.querySelectorAll('.maskReadMore')
	maskElements.forEach(el => {
		const htmlEl = el as HTMLElement
		htmlEl.dataset.originalMaxHeight = htmlEl.style.maxHeight || ''
		htmlEl.dataset.originalOverflow = htmlEl.style.overflow || ''
		htmlEl.dataset.originalMask = htmlEl.style.mask || ''
		htmlEl.dataset.originalWebkitMask = htmlEl.style.webkitMask || ''

		htmlEl.style.maxHeight = 'none'
		htmlEl.style.overflow = 'visible'
		htmlEl.style.mask = 'none'
		htmlEl.style.webkitMask = 'none'
	})
}

function restoreElements() {
	const selectors = [
		'.article-actions',
		'.main-nav',
		'nav',
		'.block-nav',
		'.read-more-btn',
		'footer',
		'.footer',
		'.block-footer',
		'.bot-footer',
		'.hidePrint',
	]

	selectors.forEach(selector => {
		const elements = document.querySelectorAll(selector)
		elements.forEach(el => {
			const htmlEl = el as HTMLElement
			htmlEl.style.display = htmlEl.dataset.originalDisplay || ''
			delete htmlEl.dataset.originalDisplay
		})
	})

	const maskElements = document.querySelectorAll('.maskReadMore')
	maskElements.forEach(el => {
		const htmlEl = el as HTMLElement
		htmlEl.style.maxHeight = htmlEl.dataset.originalMaxHeight || ''
		htmlEl.style.overflow = htmlEl.dataset.originalOverflow || ''
		htmlEl.style.mask = htmlEl.dataset.originalMask || ''
		htmlEl.style.webkitMask = htmlEl.dataset.originalWebkitMask || ''

		delete htmlEl.dataset.originalMaxHeight
		delete htmlEl.dataset.originalOverflow
		delete htmlEl.dataset.originalMask
		delete htmlEl.dataset.originalWebkitMask
	})
}

onMounted(() => {
	window.addEventListener('beforeprint', hidePrintElements)
	window.addEventListener('afterprint', restoreElements)
	window.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
	window.removeEventListener('beforeprint', hidePrintElements)
	window.removeEventListener('afterprint', restoreElements)
	window.removeEventListener('click', handleClickOutside)
})

function toggleShareDropdown() {
	showShareDropdown.value = !showShareDropdown.value
}

function showDropdown() {
	if (hideTimeout) {
		clearTimeout(hideTimeout)
		hideTimeout = null
	}
	showShareDropdown.value = true
}

function hideDropdown() {
	hideTimeout = setTimeout(() => {
		showShareDropdown.value = false
		hideTimeout = null
	}, 300)
}

function getCurrentDate() {
	const now = new Date()
	const months = [
		'January',
		'February',
		'March',
		'April',
		'May',
		'June',
		'July',
		'August',
		'September',
		'October',
		'November',
		'December',
	]
	return {
		year: now.getFullYear(),
		month: months[now.getMonth()],
		day: now.getDate(),
	}
}

function getPageTitle() {
	const editorialTitle = articleTitleText(boundRecord.value?.title || boundRecord.value?.name)
	if (editorialTitle) return editorialTitle
	let title = document.title.replace(' | TheTorah.com', '').replace(' - TheTorah.com', '')
	title = title.replace('TheTorah.com - Academic Biblical Scholarship', '').trim()
	return title || 'Article from TheTorah.com'
}

const citationRoute = useRoute()

function generateSblCitation() {
	const title = getPageTitle()
	const url = canonicalUrl(citationRoute.path, boundRecord.value?.canonical)
	const currentDate = getCurrentDate()
	return `"${title}." TheTorah.com. Accessed ${currentDate.month} ${currentDate.day}, ${currentDate.year}. ${url}.`
}

function generateApaCitation() {
	const title = getPageTitle()
	const url = canonicalUrl(citationRoute.path, boundRecord.value?.canonical)
	const currentDate = getCurrentDate()
	return `"${title}" (n.d.). TheTorah.com. Retrieved ${currentDate.month} ${currentDate.day}, ${currentDate.year}, from ${url}`
}

function copyCitation(citation: string, type: string) {
	navigator.clipboard.writeText(citation).then(() => {
		console.log('Citation copied to clipboard')
		copiedCitation.value = type
		setTimeout(() => {
			copiedCitation.value = null
		}, 10000)
	})
}

function shareToFacebook() {
	const url = window.location.href
	const { title } = document

	try {
		navigator
			.share({
				title,
				text: 'Check out this article from TheTorah.com',
				url,
			})
			.catch((err: unknown) => {
				console.log('Error sharing:', err)
				navigator.clipboard.writeText(url).then(() => {
					console.log('Link copied to clipboard')
				})
			})
	} catch {
		navigator.clipboard.writeText(url).then(() => {
			console.log('Link copied to clipboard')
		})
	}
	showShareDropdown.value = false
}

function shareToTwitter() {
	const encodedUrl = encodeURIComponent(window.location.href)
	const title = encodeURIComponent(document.title)
	window.open(
		`https://twitter.com/intent/tweet?url=${encodedUrl}&text=${title}`,
		'_blank',
		'width=600,height=400',
	)
	showShareDropdown.value = false
}

function shareByEmail() {
	const encodedUrl = encodeURIComponent(window.location.href)
	const title = encodeURIComponent(document.title)
	const body = encodeURIComponent(`Check out this article: ${encodedUrl}`)
	window.open(`mailto:?subject=${title}&body=${body}`)
	showShareDropdown.value = false
}

function handleCite() {
	showCiteModal.value = true
}

function handleClickOutside(event: Event) {
	const target = event.target as Element
	if (!target.closest('.share-container')) {
		showShareDropdown.value = false
	}
}
</script>

<template>
	<div class="flex gap-1" :class="props.layout === 'vertical' ? 'column m_row' : ''">
		<button
			v-if="props.showPrint"
			class="redhover opacity-5 pointer flex column border-none bg-transparent"
			@click="handlePrint"
		>
			<Icon size="1.6" weight="300" name="print" class="line-height-1" />
			Print
		</button>

		<div
			v-if="props.showShare"
			class="share-container relative"
			@mouseleave="hideDropdown"
			@focusout="hideDropdown"
			@mouseenter="showDropdown"
			@focusin="showDropdown"
		>
			<button
				class="redhover opacity-5 pointer flex column border-none bg-transparent"
				:class="{ active: showShareDropdown }"
				@click="toggleShareDropdown"
				@mouseover="showDropdown"
				@focus="showDropdown"
			>
				<Icon size="1.6" weight="300" name="share" class="line-height-1" />
				Share
			</button>

			<div v-if="showShareDropdown" class="absolute grid grid-wrap-3 share-dropdown gap-05">
				<Btn class="color-white px-1-5" flat @click="shareToFacebook">
					<Icon size="1.6" weight="300" name="facebook-f" class="line-height-1" />
				</Btn>
				<Btn class="color-white px-1-5" flat @click="shareToTwitter">
					<Icon size="1.6" weight="300" name="x-twitter" class="line-height-1" />
				</Btn>
				<Btn class="color-white px-1-5" flat @click="shareByEmail">
					<Icon size="1.6" weight="300" name="email" class="line-height-1" />
				</Btn>
			</div>
		</div>

		<button
			v-if="props.showCite"
			class="redhover opacity-5 pointer flex column border-none bg-transparent"
			@click="handleCite"
		>
			<Icon size="1.6" weight="300" name="news" class="line-height-1" />
			Cite
		</button>

		<!-- Citation Modal -->
		<Dialog :open="showCiteModal" @update:open="showCiteModal = $event">
			<div class="flex space-between">
				<p class="bold">SBL Style</p>
				<small
					class="txt12 flex gap-025"
					:class="{ 'opacity-7': copiedCitation === null, 'color-green': copiedCitation === 'sbl' }"
				>
					<Icon size="0.9" weight="300" name="content_copy" class="line-height-1" />
					{{ copiedCitation === 'sbl' ? 'Copied!' : 'Click to copy' }}
				</small>
			</div>
			<div class="bg-gray p-1 hover" @click="copyCitation(generateSblCitation(), 'sbl')">
				<p class="txt14">
					{{ generateSblCitation() }}
				</p>
			</div>
			<div class="flex space-between pt-2">
				<p class="bold">APA Style</p>
				<small
					class="txt12 flex gap-025"
					:class="{ 'opacity-7': copiedCitation === null, 'color-green': copiedCitation === 'apa' }"
				>
					<Icon size="0.9" weight="300" name="content_copy" class="line-height-1" />
					{{ copiedCitation === 'apa' ? 'Copied!' : 'Click to copy' }}
				</small>
			</div>
			<div class="bg-gray p-1 hover" @click="copyCitation(generateApaCitation(), 'apa')">
				<p class="txt14">
					{{ generateApaCitation() }}
				</p>
			</div>
		</Dialog>
	</div>
</template>

<style scoped>
.share-dropdown {
	top: 100%;
	left: 50%;
	transform: translateX(-50%);
	background: #333;
	border-radius: 8px;
	padding: 8px;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
	z-index: 1000;
	min-width: 160px;
	margin-top: 8px;
}

.share-dropdown::before {
	content: '';
	position: absolute;
	top: -6px;
	left: 50%;
	transform: translateX(-50%);
	border-left: 6px solid transparent;
	border-right: 6px solid transparent;
	border-bottom: 6px solid #333;
}
</style>
